import React, { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import DetailsBox from '../../../components/Cards/DetailsBox'
import API from '../../../services/API'
import { Box } from '@mui/material'

const GROUPING_OPTIONS = [
  { value: 'perJourney', label: 'One invoice per journey' },
  { value: 'perBooking', label: 'One invoice per booking' },
  { value: 'perPatient', label: 'One invoice per patient (default)' },
  { value: 'consolidatedWeekly', label: 'Consolidated weekly' },
  { value: 'consolidatedMonthly', label: 'Consolidated monthly' },
]

// Commercial terms for a PTS organisation: which tariff prices their work and
// how invoices are grouped. No contract = tenant default tariff, per booking.
const PTSContractSection = ({ client }) => {
  const [tariffs, setTariffs] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const [archivedContractTariff, setArchivedContractTariff] = useState(null)
  const [form, setForm] = useState({
    tariff: null,
    invoiceGrouping: 'perPatient',
    poRequired: false,
    poNumber: '',
    paymentTermsDays: '',
    requiresQuote: false,
    notes: '',
    slaTargets: {
      onTimePickupPct: '',
      onTimeArrivalPct: '',
      maxAbortedPct: '',
      maxNoShowPct: '',
    },
  })

  const setSlaTarget = (field, value) =>
    setForm((prev) => ({
      ...prev,
      slaTargets: { ...prev.slaTargets, [field]: value },
    }))

  useEffect(() => {
    if (!client?._id) return
    setLoading(true)
    setArchivedContractTariff(null)
    Promise.all([
      API.get('/pts/tariffs?activeOnly=true'),
      API.get(`/pts/organisations/${client._id}/contract`),
    ])
      .then(([tariffRes, contractRes]) => {
        const tariffOptions =
          tariffRes.status === 200 ? tariffRes.data || [] : []
        setTariffs(tariffOptions)
        if (contractRes.status === 200 && contractRes.data) {
          const contract = contractRes.data
          const contractTariff =
            tariffOptions.find(
              (tariff) =>
                tariff._id === (contract.tariff?._id || contract.tariff),
            ) || null
          if (contract.tariff && !contractTariff) {
            setArchivedContractTariff(contract.tariff)
          }
          setForm({
            tariff: contractTariff,
            invoiceGrouping: contract.invoiceGrouping || 'perPatient',
            poRequired: !!contract.poRequired,
            poNumber: contract.poNumber || '',
            paymentTermsDays: contract.paymentTermsDays ?? '',
            requiresQuote: !!contract.requiresQuote,
            notes: contract.notes || '',
            slaTargets: {
              onTimePickupPct: contract.slaTargets?.onTimePickupPct ?? '',
              onTimeArrivalPct: contract.slaTargets?.onTimeArrivalPct ?? '',
              maxAbortedPct: contract.slaTargets?.maxAbortedPct ?? '',
              maxNoShowPct: contract.slaTargets?.maxNoShowPct ?? '',
            },
          })
        }
      })
      .catch(() => setError('Unable to load contract details.'))
      .finally(() => setLoading(false))
  }, [client?._id])

  const save = async () => {
    setSaving(true)
    setError('')
    setSaved(false)
    try {
      const res = await API.put(`/pts/organisations/${client._id}/contract`, {
        ...form,
        tariff: form.tariff?._id || null,
      })
      if (res.status === 200) {
        setSaved(true)
      } else {
        setError(res.data?.error || 'Unable to save contract terms.')
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Unable to save contract terms.')
    } finally {
      setSaving(false)
    }
  }

  if (client?.orgType !== 'PTS') return null

  return (
    <DetailsBox
      title="PTS Contract & Tariff"
      caption="How this organisation's transport work is priced and invoiced."
    >
      <Box>
        <Grid container spacing={2}>
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          {saved && (
            <Grid item xs={12}>
              <Alert severity="success" onClose={() => setSaved(false)}>
                Contract terms saved.
              </Alert>
            </Grid>
          )}
          {archivedContractTariff && (
            <Grid item xs={12}>
              <Alert severity="warning">
                This organisation was using the archived tariff{' '}
                <strong>{archivedContractTariff.name}</strong>. Archived
                tariffs are not available for future bookings; choose an active
                tariff or leave the field blank to use the tenant default.
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={tariffs}
              loading={loading}
              getOptionLabel={(option) =>
                `${option.name}${option.isDefault ? ' (default)' : ''}`
              }
              isOptionEqualToValue={(option, value) =>
                option._id === value?._id
              }
              value={form.tariff}
              onChange={(e, value) =>
                setForm((prev) => ({ ...prev, tariff: value }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tariff"
                  size="small"
                  helperText="Leave empty to use the tenant default tariff"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Invoice grouping"
              size="small"
              fullWidth
              value={form.invoiceGrouping}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  invoiceGrouping: e.target.value,
                }))
              }
              helperText="Consolidated billing runs arrive in a later release — the preference is stored now"
            >
              {GROUPING_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              label="Payment terms (days)"
              type="number"
              size="small"
              fullWidth
              value={form.paymentTermsDays}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  paymentTermsDays: e.target.value,
                }))
              }
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              label="PO number"
              size="small"
              fullWidth
              value={form.poNumber}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, poNumber: e.target.value }))
              }
              disabled={!form.poRequired}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.poRequired}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      poRequired: e.target.checked,
                    }))
                  }
                />
              }
              label="PO required"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.requiresQuote}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      requiresQuote: e.target.checked,
                    }))
                  }
                />
              }
              label="Quote required before planning"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">
              SLA targets (optional — leave blank to skip a target on the
              performance pack)
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              label="On-time pickup (min %)"
              type="number"
              size="small"
              fullWidth
              value={form.slaTargets.onTimePickupPct}
              onChange={(e) => setSlaTarget('onTimePickupPct', e.target.value)}
              inputProps={{ min: 0, max: 100 }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              label="On-time arrival (min %)"
              type="number"
              size="small"
              fullWidth
              value={form.slaTargets.onTimeArrivalPct}
              onChange={(e) => setSlaTarget('onTimeArrivalPct', e.target.value)}
              inputProps={{ min: 0, max: 100 }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              label="Max aborted (%)"
              type="number"
              size="small"
              fullWidth
              value={form.slaTargets.maxAbortedPct}
              onChange={(e) => setSlaTarget('maxAbortedPct', e.target.value)}
              inputProps={{ min: 0, max: 100 }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              label="Max no-show (%)"
              type="number"
              size="small"
              fullWidth
              value={form.slaTargets.maxNoShowPct}
              onChange={(e) => setSlaTarget('maxNoShowPct', e.target.value)}
              inputProps={{ min: 0, max: 100 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Contract notes"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.notes}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, notes: e.target.value }))
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={save}
              disabled={saving || loading}
            >
              {saving ? 'Saving...' : 'Save Contract Terms'}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </DetailsBox>
  )
}

export default PTSContractSection
